/**
 * Baby Mode Dashboard
 *
 * Dashboard for premium users after birth (Baby Mode).
 * Shows baby age counter, organization tools: checklists, calendar, names, chat, etc.
 */

import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Loader2,
  Scale,
  Ruler,
  Circle,
  Library,
} from 'lucide-react';
import { useTranslation, useLocale } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { SSWCircle } from '@/components/SSWCircle';
import { Pregnancy } from '@/store/pregnancyStore';
import { useAuth } from '@/features/auth';
import { ActivityFeedWidget } from '@/features/activity-feed';
import { isValidUUID } from '@/lib/utils';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { ActiveSleepTimerCard } from '@/features/baby-care';
import { useBabyGrowth } from '@/features/baby-growth';
import { useBabyBlogPosts } from '@/features/blog/hooks/useBlogPosts';
import { isWissenswertesEnabled } from '@/services/systemSettings';
import { RecommendationSlot, useRandomInsertionIndex } from '@/features/recommendations';
import { calculateBabyAgeParts } from '@/lib/babyAge';

// Helper to get the appropriate greeting based on time of day
function getTimeSlot(): string {
  const hour = new Date().getHours();
  if (hour >= 0 && hour < 6) return '00-06';
  if (hour >= 6 && hour < 11) return '06-11';
  if (hour >= 11 && hour < 13) return '11-13';
  if (hour >= 13 && hour < 18) return '13-18';
  return '18-00';
}

interface BabyModeDashboardProps {
  lastCompletedPregnancy: Pregnancy | null;
}

interface ChecklistSummary {
  id: string;
  title: string;
  completed_count: number;
  total_count: number;
  created_at: string;
}

interface AppointmentSummary {
  id: string;
  title: string;
  appointment_date: string;
  appointment_time: string | null;
}

// Helper to format growth values with appropriate units
// Weight: always in grams, Length: cm or m
function formatGrowthValue(value: number | null | undefined, type: 'weight' | 'length'): { value: string; unit: string } {
  if (value === null || value === undefined) {
    return { value: '-', unit: type === 'weight' ? 'g' : 'cm' };
  }

  if (type === 'weight') {
    // Weight always in grams
    return { value: Math.round(value).toString(), unit: 'g' };
  } else {
    // Length in cm, convert to m at 100cm
    if (value >= 100) {
      return { value: (value / 100).toFixed(2), unit: 'm' };
    }
    return { value: value.toFixed(1), unit: 'cm' };
  }
}

export function BabyModeDashboard({ lastCompletedPregnancy }: BabyModeDashboardProps) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('dashboard');
  const { locale } = useLocale();
  const [checklists, setChecklists] = useState<ChecklistSummary[]>([]);
  const [appointments, setAppointments] = useState<AppointmentSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [greeting, setGreeting] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [showWissenswertes, setShowWissenswertes] = useState(false);

  // Determine if it was a birth or neutral end
  const wasBirth = lastCompletedPregnancy?.completion_type === 'birth';

  // Get the pregnancy ID for data queries (validated to be a proper UUID)
  const rawPregnancyId = lastCompletedPregnancy?.id;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  // Check if Baby Care feature is enabled
  const isBabyCareEnabled = useFeatureEnabled('baby_care_enabled');

  // Get baby growth data
  const { entries: growthEntries, isLoading: isGrowthLoading } = useBabyGrowth();

  // Find last recorded value for each growth category (entries are sorted by date descending)
  const latestGrowthValues = {
    weight: growthEntries.find(e => e.weight_grams !== null)?.weight_grams ?? null,
    height: growthEntries.find(e => e.height_cm !== null)?.height_cm ?? null,
    head: growthEntries.find(e => e.head_circumference_cm !== null)?.head_circumference_cm ?? null,
  };

  // Baby age in days for blog filtering
  const birthDate = lastCompletedPregnancy?.actual_birth_date
    ? new Date(lastCompletedPregnancy.actual_birth_date)
    : null;
  const babyAgeDays = birthDate
    ? Math.floor((new Date().getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24))
    : undefined;

  // Blog posts for baby mode
  const { posts: blogPosts, isLoading: blogLoading } = useBabyBlogPosts({
    babyAgeDays,
    limit: 5,
  });
  // Random injection index for the product recommendation among the blog posts
  const wissenswertesRecIndex = useRandomInsertionIndex('wissenswertes_baby', blogPosts.length);

  // Fetch Wissenswertes visibility setting
  useEffect(() => {
    isWissenswertesEnabled().then(setShowWissenswertes);
  }, []);

  // Locale for date formatting
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';

  // Fetch greeting from database (locale-aware)
  useEffect(() => {
    async function fetchGreeting() {
      const timeSlot = getTimeSlot();
      const { data } = await supabase
        .from('dashboard_greetings')
        .select('greeting_text')
        .eq('time_slot', timeSlot)
        .eq('locale', locale)
        .maybeSingle();

      if (data?.greeting_text) {
        setGreeting(data.greeting_text);
      } else {
        // Default fallback greetings per locale
        const defaults: Record<string, Record<string, string>> = {
          de: {
            '00-06': 'Gute Nacht, {name}',
            '06-11': 'Guten Morgen, {name}',
            '11-13': 'Mahlzeit, {name}',
            '13-18': 'Guten Tag, {name}',
            '18-00': 'Guten Abend, {name}',
          },
          en: {
            '00-06': 'Good night, {name}',
            '06-11': 'Good morning, {name}',
            '11-13': 'Enjoy your lunch, {name}',
            '13-18': 'Good afternoon, {name}',
            '18-00': 'Good evening, {name}',
          },
        };
        const localeDefaults = defaults[locale] || defaults.de;
        setGreeting(localeDefaults[timeSlot] || 'Hallo, {name}');
      }
    }

    fetchGreeting();
  }, [locale]);

  // Fetch user's first name
  useEffect(() => {
    async function fetchFirstName() {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('first_name, display_name')
        .eq('id', user.id)
        .maybeSingle();
      // Use first_name if available, otherwise extract from display_name or use empty
      const name = data?.first_name || data?.display_name?.split(' ')[0] || '';
      setFirstName(name);
    }
    fetchFirstName();
  }, [user]);

  // Format greeting with first name only
  const formattedGreeting = greeting.replace('{name}', firstName || t('greetings.you'));

  // Fetch data on mount
  useEffect(() => {
    async function fetchData() {
      if (!user) return;

      setIsLoading(true);

      try {
        // Only fetch checklists if we have a pregnancy ID
        if (pregnancyId) {
          // First, fetch the checklists (note: table has created_at, not updated_at)
          const { data: checklistData, error: checklistError } = await supabase
            .from('user_checklists')
            .select('id, title, created_at')
            .eq('pregnancy_id', pregnancyId)
            .order('created_at', { ascending: false })
            .limit(3);

          if (checklistError) {
            console.error('Error fetching checklists:', checklistError);
          } else if (checklistData && checklistData.length > 0) {
            // Fetch items for these checklists
            const checklistIds = checklistData.map(c => c.id);
            const { data: itemsData } = await supabase
              .from('user_checklist_items')
              .select('id, checklist_id, status')
              .in('checklist_id', checklistIds);

            // Group items by checklist and calculate summaries
            const itemsByChecklist = new Map<string, Array<{ status: string }>>();
            (itemsData || []).forEach(item => {
              const existing = itemsByChecklist.get(item.checklist_id) || [];
              existing.push({ status: item.status });
              itemsByChecklist.set(item.checklist_id, existing);
            });

            const summaries: ChecklistSummary[] = checklistData.map(cl => {
              const items = itemsByChecklist.get(cl.id) || [];
              return {
                id: cl.id,
                title: cl.title,
                completed_count: items.filter(i => i.status === 'completed').length,
                total_count: items.length,
                created_at: cl.created_at,
              };
            });
            setChecklists(summaries);
          }
        }

        // Fetch upcoming appointments (filter by pregnancy if available)
        const today = new Date().toISOString().split('T')[0];
        let aptQuery = supabase
          .from('user_appointments')
          .select('id, title, appointment_date, appointment_time')
          .gte('appointment_date', today)
          .order('appointment_date', { ascending: true })
          .limit(3);

        if (pregnancyId) {
          aptQuery = aptQuery.eq('pregnancy_id', pregnancyId);
        }

        const { data: aptData } = await aptQuery;

        if (aptData) {
          setAppointments(aptData);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [user, lastCompletedPregnancy?.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Greeting */}
      <h1 className="text-white text-3xl font-bold text-left mb-6">
        {formattedGreeting}
      </h1>

      {/* SSW Section - Shows completion state */}
      <section className="flex flex-col items-center">
        {wasBirth ? (
          <>
            <BirthCompletedCircle pregnancy={lastCompletedPregnancy!} />

            {/* Growth Tracker Values */}
            {!isGrowthLoading && growthEntries.length > 0 && (
              <div className="flex justify-center gap-6 mt-6 w-full">
                {/* Weight */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-1 mb-1">
                    <Scale className="w-4 h-4 text-white/60" />
                    <span className="text-white/60 text-xs">{t('growth.weight')}</span>
                  </div>
                  <span className="text-white text-xl font-bold">
                    {formatGrowthValue(latestGrowthValues.weight, 'weight').value}
                  </span>
                  <span className="text-white/60 text-xs">
                    {formatGrowthValue(latestGrowthValues.weight, 'weight').unit}
                  </span>
                </div>

                {/* Height */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-1 mb-1">
                    <Ruler className="w-4 h-4 text-white/60" />
                    <span className="text-white/60 text-xs">{t('growth.height')}</span>
                  </div>
                  <span className="text-white text-xl font-bold">
                    {formatGrowthValue(latestGrowthValues.height, 'length').value}
                  </span>
                  <span className="text-white/60 text-xs">
                    {formatGrowthValue(latestGrowthValues.height, 'length').unit}
                  </span>
                </div>

                {/* Head Circumference */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-1 mb-1">
                    <Circle className="w-4 h-4 text-white/60" />
                    <span className="text-white/60 text-xs">{t('growth.head')}</span>
                  </div>
                  <span className="text-white text-xl font-bold">
                    {formatGrowthValue(latestGrowthValues.head, 'length').value}
                  </span>
                  <span className="text-white/60 text-xs">
                    {formatGrowthValue(latestGrowthValues.head, 'length').unit}
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <SSWCircle
              currentSSW={0}
              dayInWeek={0}
              babyNames={[]}
              overrideProgress={0}
              overrideLabel="SSW 0"
            />
            <div className="w-full mt-8 text-center">
              <p className="text-white/70 text-lg">{t('pregnancy.noActive')}</p>
            </div>
          </>
        )}
      </section>

      {/* Active Sleep Timer - shown below progress circle */}
      {wasBirth && isBabyCareEnabled && (
        <div className="mt-6">
          <ActiveSleepTimerCard compact />
        </div>
      )}



      {/* Anstehende Termine Section */}
      <section className="mt-16">
        <h2 className="text-white text-2xl font-bold mb-4">{t('appointments.title')}</h2>

        <div className="space-y-5">
          {appointments.length > 0 ? (
            <>
              {appointments.map((apt) => {
                const aptDate = new Date(apt.appointment_date);
                const day = aptDate.toLocaleDateString(dateLocale, { day: 'numeric' });
                const month = aptDate.toLocaleDateString(dateLocale, { month: 'short' }).toUpperCase().replace('.', '');
                const weekday = aptDate.toLocaleDateString(dateLocale, { weekday: 'long' });
                const time = apt.appointment_time ? t('appointments.atTime', { time: apt.appointment_time }) : '';

                return (
                  <div key={apt.id} className="flex items-center gap-4">
                    <div className="date-box">
                      <span className="date-day text-white">{day}</span>
                      <span className="date-month text-white">{month}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{apt.title}</p>
                      <p className="text-white/70 text-sm">{t('appointments.onDay', { weekday, time })}</p>
                    </div>
                  </div>
                );
              })}

              {/* Alle anzeigen Button */}
              <Link to="/calendar" className="block mt-5">
                <button className="w-full py-3 bg-pp-orange rounded-pp flex items-center justify-center gap-2 text-white font-semibold text-base">
                  {t('appointments.showAll')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </Link>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-white/70 mb-4">{t('appointments.none')}</p>
              <Link to="/calendar">
                <button className="px-6 py-3 bg-pp-orange rounded-pp text-white font-medium">
                  {t('appointments.add')}
                </button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Active Checklists Section */}
      <section className="mt-16">
        <h2 className="text-white text-2xl font-bold mb-4">{t('checklists.title')}</h2>

        <div className="space-y-3">
          {checklists.length > 0 ? (
            <>
              {checklists.map(cl => (
                <Link key={cl.id} to={`/tools/checklists/${cl.id}`}>
                  <div className="glass-effect rounded-[15px] p-4 hover:bg-white/15 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{cl.title}</p>
                        <p className="text-white/60 text-sm">
                          {t('checklists.completed', { completed: cl.completed_count, total: cl.total_count })}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-pp-orange" />
                    </div>
                  </div>
                </Link>
              ))}

              {/* Alle anzeigen Button */}
              <Link to="/tools/checklists" className="block mt-2">
                <button className="w-full py-3 bg-pp-orange rounded-pp flex items-center justify-center gap-2 text-white font-semibold text-base">
                  {t('checklists.showAll')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </Link>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-white/60 text-sm">{t('checklists.none')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Wissenswertes Section - Blog posts for baby mode */}
      {showWissenswertes && (
        <section className="mt-16">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Library className="w-8 h-8 text-white" />
              <h2 className="text-white text-2xl font-medium">{t('blog.title')}</h2>
            </div>
            <Link
              to="/blog"
              className="px-3 py-1.5 bg-pp-orange rounded-[5px] text-white text-sm font-medium"
            >
              {t('blog.showAll')}
            </Link>
          </div>

          <div className="space-y-4">
            {blogLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white" />
              </div>
            ) : blogPosts.length > 0 ? (
              <>
                {/* Start position */}
                <RecommendationSlot placement="wissenswertes_baby" position="start" />
                {blogPosts.map((post, idx) => (
                  <Fragment key={post.id}>
                    {idx === wissenswertesRecIndex && (
                      <RecommendationSlot placement="wissenswertes_baby" position="random" />
                    )}
                    <Link
                      to={`/blog/${post.slug}`}
                      className="block glass-effect rounded-[15px] overflow-hidden hover:bg-white/15 transition-all"
                    >
                      {post.image_url && (
                        <div className="aspect-video w-full overflow-hidden">
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-full object-cover object-center"
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {post.category?.name && (
                            <span className="inline-block text-xs font-medium px-2 py-0.5 bg-pp-teal border border-white/25 rounded-[5px] text-white">
                              {post.category.name}
                            </span>
                          )}
                        </div>
                        <h2 className="font-semibold text-white mb-1">{post.title}</h2>
                        <div className="flex items-center justify-end mt-3">
                          <ChevronRight className="w-4 h-4 text-pp-orange" />
                        </div>
                      </div>
                    </Link>
                  </Fragment>
                ))}
                {wissenswertesRecIndex === blogPosts.length && (
                  <RecommendationSlot placement="wissenswertes_baby" position="random" />
                )}
                <RecommendationSlot placement="wissenswertes_baby" position="end" />
              </>
            ) : (
              <div className="glass-effect rounded-[15px] p-6 text-center text-white/60">
                <p>{t('blog.noArticles')}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Activity Feed Section */}
      <ActivityFeedWidget />
    </>
  );
}

// Component for birth completed state
function BirthCompletedCircle({ pregnancy }: { pregnancy: Pregnancy }) {
  const { t, i18n } = useTranslation('dashboard');

  const birthDate = pregnancy.actual_birth_date
    ? new Date(pregnancy.actual_birth_date)
    : null;

  const age = birthDate ? calculateBabyAgeParts(birthDate) : { years: 0, months: 0, weeks: 0, days: 0 };

  const babyNames = (() => {
    if (pregnancy.baby_data?.babies) {
      return pregnancy.baby_data.babies
        .filter(b => b.name)
        .map(b => ({
          name: b.name!,
          gender: (b.gender === 'male' || b.gender === 'female' ? b.gender : 'neutral') as 'male' | 'female' | 'neutral',
        }));
    }
    if (pregnancy.baby_names && Array.isArray(pregnancy.baby_names)) {
      return pregnancy.baby_names.map((n: string | { name: string; gender?: string }) => {
        if (typeof n === 'string') {
          return { name: n, gender: 'neutral' as const };
        }
        const gender = n.gender === 'male' || n.gender === 'female' ? n.gender : 'neutral';
        return { name: n.name, gender: gender as 'male' | 'female' | 'neutral' };
      });
    }
    return [];
  })();

  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';

  const birthDateFormatted = birthDate
    ? birthDate.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';


  // Build age display: x Jahre, y Monate, z Wochen, w Tage (top to bottom)
  // Only show each unit if > 0
  const getAgeDisplay = () => {
    const parts: string[] = [];

    if (age.years > 0) {
      parts.push(t('babyAge.year', { count: age.years }));
    }

    if (age.months > 0) {
      parts.push(t('babyAge.month', { count: age.months }));
    }

    if (age.weeks > 0) {
      parts.push(t('babyAge.week', { count: age.weeks }));
    }

    if (age.days > 0) {
      parts.push(t('babyAge.day', { count: age.days }));
    }

    if (parts.length === 0) {
      return t('babyAge.zeroDays');
    }

    return parts.join('\n');
  };

  // Custom content for the circle with counter
  const circleContent = (
    <div className="flex flex-col items-center text-center" style={{ lineHeight: '1.3' }}>
      <p className="text-white/70 text-sm mb-1">
        {t('babyAge.since')} <span className="font-bold text-white">{birthDateFormatted}</span>
      </p>
      <div className="text-white text-2xl font-bold whitespace-pre-line">{getAgeDisplay()}</div>
    </div>
  );

  return (
    <SSWCircle
      currentSSW={40}
      dayInWeek={6}
      babyNames={babyNames}
      overrideProgress={100}
      overrideContent={circleContent}
    />
  );
}

export default BabyModeDashboard;
